// 投稿フォームの本文欄について「ユーザーが一度でも手を入れたか」を boolean 1 つで保持する。
//
// 設計判断:
// - 内容の差分比較は行わない。変更イベントでフラグを立てるだけなので、画像挿入やブロック操作も
//   リッチテキストの厳密な差分判定なしに拾える (COMMUNITY-4690)。
// - 判定対象は本文エディタのみ。タイトル / アイキャッチ画像 / 添付ファイルは含めない。
// - テンプレート適用のようにプログラムから本文を書き換える場合は `suppress()` で囲む。
//   SunEditor の `setContents` は同期的に onChange を発火するため、同期フラグで確実に抑止できる。
// - 初期値は「本文が空でない、かつサーバがテンプレートをプリフィルしたのではない」で決める。
//   編集画面や下書きの再編集では保存済み本文が入っているので最初から編集済み扱いになり、
//   本文に触れないままカテゴリを変えても無警告で消えない。

// SunEditor 生成完了の通知。init.ts が dispatch している (既存箇所と同じ文字列)。
const EDITOR_READY_EVENT = 'suneditor:ready';

// 本文欄の候補。素の textarea (Q&A / トークのマークダウン) と
// SunEditor の下敷きになる textarea (トーク / お知らせ) の両方を対象にする。
const BODY_SELECTOR = '.js-category-template-body, textarea[data-editor-id]';

// SunEditor は window.suneditor に 1 インスタンスだけ持つ設計なので、
// onChange / onload を多重ラップしないよう購読者リストをエディタ側に持たせる。
const LISTENERS_KEY = '__coorumBodyDirtyListeners';
const LOADED_LISTENERS_KEY = '__coorumBodyLoadedListeners';

class PostBodyDirtyTracker {
  #container;
  #dirty;
  #initialDirty;
  #suppressed = false;
  // 初期化直後は、編集画面で保存済み本文を流し込む setContents が同期的に onChange を発火する。
  // これを「ユーザーの変更」と誤検知しないよう、同期処理が落ち着く次のマクロタスクまで無視する。
  #initializing = true;
  #markDirty;
  #resetToInitial;

  // container を渡すと本文欄の探索範囲を絞れる (誘導モーダル等)。
  // initialDirty を明示するとサーバ由来の自動判定を上書きする (主にテスト用)。
  constructor(container = document, { initialDirty = null } = {}) {
    this.#container = container;
    this.#markDirty = () => {
      if (this.#suppressed || this.#initializing) return;
      this.#dirty = true;
    };
    this.#resetToInitial = () => {
      this.#dirty = this.#initialDirty;
    };

    this.#initialDirty =
      initialDirty === null ? this.#detectInitialDirty() : initialDirty;
    this.#dirty = this.#initialDirty;

    this.#bindPlainTextareas();
    this.#bindRichEditor();

    setTimeout(() => {
      this.#initializing = false;
    }, 0);
  }

  get isDirty() {
    return this.#dirty;
  }

  // テンプレート適用後など「ここからは未編集」と宣言したいときに呼ぶ。
  reset() {
    this.#dirty = false;
  }

  // fn の実行中に起きた変更イベントを無視する。同期処理のみを想定している。
  suppress(fn) {
    this.#suppressed = true;
    try {
      return fn();
    } finally {
      this.#suppressed = false;
    }
  }

  #bodyElements() {
    return Array.from(this.#container.querySelectorAll(BODY_SELECTOR));
  }

  #detectInitialDirty() {
    const bodies = this.#bodyElements();
    if (bodies.length === 0) return false;

    // サーバがカテゴリテンプレートを流し込んだ初期表示は「未編集」として扱う。
    const form = bodies[0].closest('form');
    if (form?.dataset.bodyFromTemplate === 'true') return false;

    return bodies.some((el) => (el.value ?? '').trim() !== '');
  }

  // 素の textarea はユーザー入力で input が飛ぶ。プログラムからの value 代入では飛ばないので、
  // テンプレート適用を誤検知しない。委譲にしておくと後から差し込まれた本文欄にも効く。
  #bindPlainTextareas() {
    this.#container.addEventListener('input', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.matches(BODY_SELECTOR)) return;
      this.#markDirty();
    });
  }

  // SunEditor はキー入力・ペースト・画像挿入・ブロック操作をすべて onChange に集約する。
  // 既存の onChange (init.ts が textarea へ書き戻す処理) を壊さないよう先に呼んでから通知する。
  //
  // onload も併せてフックする。SunEditor は onload を setTimeout 経由で遅延実行し
  // (node_modules/suneditor/src/lib/core.js:6326)、その中で初期内容に対する onChange を発火するため、
  // コンストラクタの 1 マクロタスク分の抑止では間に合わない。onload 完了までを初期化とみなす。
  #bindRichEditor() {
    const attach = (editor) => {
      if (!editor) return;

      if (!editor[LISTENERS_KEY]) {
        editor[LISTENERS_KEY] = [];
        const original = editor.onChange;
        editor.onChange = (contents, core) => {
          original?.call(editor, contents, core);
          editor[LISTENERS_KEY].forEach((listener) => listener());
        };
      }
      if (!editor[LISTENERS_KEY].includes(this.#markDirty)) {
        editor[LISTENERS_KEY].push(this.#markDirty);
      }

      if (!editor[LOADED_LISTENERS_KEY]) {
        editor[LOADED_LISTENERS_KEY] = [];
        const originalOnload = editor.onload;
        editor.onload = (core, reload) => {
          originalOnload?.call(editor, core, reload);
          editor[LOADED_LISTENERS_KEY].forEach((listener) => listener());
        };
      }
      if (!editor[LOADED_LISTENERS_KEY].includes(this.#resetToInitial)) {
        editor[LOADED_LISTENERS_KEY].push(this.#resetToInitial);
      }
    };

    // 生成順はページによって前後するため、生成済み / これから生成の両方に備える。
    if (window.suneditor) attach(window.suneditor);
    document.addEventListener(EDITOR_READY_EVENT, (event) =>
      attach(event.detail?.editor),
    );
  }
}

export { PostBodyDirtyTracker, BODY_SELECTOR };
